import React from 'react';
import { Pagination } from 'react-bootstrap';
import styles from '../data/QueryMulti.module.css';
import { GCacheQuery, GCacheState, GCacheStore } from '../data/ReduxGCache';
import { Entity, Field, getFieldArray, QueryOptions, Row } from 'gossamer-unified';
import { getPageNumber, moveToPage } from '../data/Utils';
import { copyDataToClipboard } from '../data/copyData';
import { ListSettings } from './ListSettings';
import { GCacheQuerySectionProps } from '../data/useCacheQuery';
import { useCloneEntity } from '../ancillary/useCloneEntity';
import { useStore } from 'react-redux';

interface Props {
  // changePageSize: (newPageSize: number) => void;
  moveToPage: (newPage: number) => void;
  pageNumber: number;
  pageSize: number;
  rowsInPage: number;
}

export const Paginator = (props: Props): React.JSX.Element => {
  return (
    <Pagination>
      <Pagination.First onClick={props.moveToPage.bind(null, 1)} disabled={props.pageNumber === 1} />
      <Pagination.Prev onClick={props.moveToPage.bind(null, props.pageNumber - 1)} disabled={props.pageNumber === 1} />
      <Pagination.Item active>{props.pageNumber}</Pagination.Item>
      <Pagination.Next
        onClick={props.moveToPage.bind(null, props.pageNumber + 1)}
        disabled={props.rowsInPage < props.pageSize}
      />
    </Pagination>
  );
};

const defaultItemCountFormatter = (count: number): string => {
  if (count === 0) return 'none';
  if (count === 1) return '1 item';
  return `${count} items`;
};

interface PaginatorAndListSettingsProps<T extends Row> {
  alterEntity?: (entity: Entity<T>) => void;
  entity: Entity<T>;
  gCacheQuery: GCacheQuery<T>;
  itemCountFormatter?: (count: number) => string;
  onRowClick?: (keyValue: string) => void;
  queryOptions: QueryOptions;
  render: (props: GCacheQuerySectionProps<T>) => React.JSX.Element;
  setQueryOptions?: (newQueryOptions: QueryOptions) => void;
  showItemCount?: boolean;
  showSettingsControl?: boolean;
  showPaginator?: 'never' | 'as-required' | 'always';
}

export const PaginatorAndListSettings = <T extends Row>(props: PaginatorAndListSettingsProps<T>): React.JSX.Element => {
  const store = useStore() as GCacheStore;
  const LocalEntity = useCloneEntity(props.entity);
  const [click, setClick] = React.useState(0);
  const [pageNumber, pageSize] = getPageNumber(props.queryOptions);
  const SubSection = props.render;
  const showPaginator =
    props.showPaginator === 'always' ||
    (!!props.setQueryOptions &&
      (!props.showPaginator || props.showPaginator === 'as-required') &&
      ((props.gCacheQuery.data?.length ?? 0) >= pageSize || pageNumber > 1));

  React.useEffect(() => {
    if (window.innerWidth < 673) {
      let columnsShown = 0;
      getFieldArray(LocalEntity).forEach(([field, _]: [Field, string]) => {
        if (field.listColumn) {
          columnsShown += 1;
          if (columnsShown > 2) {
            field.listColumn = false;
          }
        }
      });
    }
  }, []);

  return (
    <>
      <SubSection
        entity={LocalEntity}
        gCacheQuery={props.gCacheQuery}
        onRowClick={props.onRowClick}
        queryOptions={props.queryOptions}
        setQueryOptions={props.setQueryOptions}
      />
      <div className={styles.QueryMultiFooter}>
        {showPaginator && !props.gCacheQuery.error && !props.gCacheQuery.isFetching && (
          <Paginator
            pageNumber={pageNumber}
            pageSize={pageSize}
            moveToPage={(newPage) => props.setQueryOptions?.(moveToPage(props.queryOptions, newPage))}
            rowsInPage={props.gCacheQuery.data?.length ?? 0}
          />
        )}
        {!showPaginator &&
          !props.gCacheQuery.error &&
          !props.gCacheQuery.isFetching &&
          props.showItemCount !== false &&
          props.gCacheQuery.data && (
            <div>
              {props.itemCountFormatter
                ? props.itemCountFormatter(props.gCacheQuery.data.length)
                : defaultItemCountFormatter(props.gCacheQuery.data.length)}
            </div>
          )}
        {props.showSettingsControl !== false &&
          !!props.setQueryOptions &&
          !props.gCacheQuery.error &&
          !props.gCacheQuery.isFetching && (
            <ListSettings
              click={() => setClick(click + 1)}
              entity={LocalEntity}
              handleCopyDataToClipboard={(includeHeader?: boolean) =>
                copyDataToClipboard(store, LocalEntity, props.gCacheQuery.data || [], !!includeHeader)
              }
              queryOptions={props.queryOptions}
              updateQueryOptions={props.setQueryOptions}
            />
          )}
      </div>
    </>
  );
};
